import { useState } from "react";
import type { ReactNode } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Cell, LabelList, LineChart as RechartsLineChart, Line, PieChart, Pie,
} from "recharts";
import { PRIMARY, VIOLET, MUTED, FG1, FG3 } from "./theme";
import { fmtN, fmtPct, pctVal } from "./helpers";
import { ChartCard } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type CardProps = {
  filterPills?: FilterPillSpec[];
  headerExtra?: ReactNode;
  // Legacy prop kept for backward compat — renders to the right of the
  // title, inline with it. Prefer filterPills/headerExtra for new code.
  titleExtra?: ReactNode;
};

type KpiTone = "primary" | "danger" | "muted" | "success";

export function EmptyState({ message }: { message: string }) {
  return <div className="empty">{message}</div>;
}

function EmptyChart({
  title, subtitle, filterPills, headerExtra, titleExtra,
}: { title: string; subtitle?: string } & CardProps) {
  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <div className="empty" style={{ padding: 24 }}>No data</div>
    </ChartCard>
  );
}

export function KpiCard({
  label, value, sub, tone, info,
  compareValue, compareSub, compareDeltaPp, compareColor,
}: {
  label: string; value: string; sub?: string; tone?: KpiTone; info?: string;
  compareValue?: string;
  compareSub?: string;
  compareDeltaPp?: number | null;
  compareColor?: string;
}) {
  const toneCls = tone ? ` is-${tone}` : "";
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const goldColor = compareColor ?? "#D4A017";
  const deltaText = compareDeltaPp == null || !Number.isFinite(compareDeltaPp)
    ? null
    : `${compareDeltaPp >= 0 ? "+" : ""}${compareDeltaPp.toFixed(1)}pp`;
  return (
    <div className={`kpi${toneCls}`}>
      <div className="kpi-label">
        {label}
        {info && (
          <span
            style={{ lineHeight: 1, cursor: "default" }}
            onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setTip(null)}
          >
            <i className="ti ti-info-circle" style={{ fontSize: 14, opacity: 0.5 }} />
          </span>
        )}
      </div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
      {compareValue !== undefined && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${goldColor}66` }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
            <span style={{ color: goldColor, fontSize: 16, fontWeight: 700 }}>{compareValue}</span>
            {deltaText && (
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: (compareDeltaPp ?? 0) >= 0 ? goldColor : FG3,
              }}>
                {deltaText}
              </span>
            )}
          </div>
          {compareSub && (
            <div style={{ color: FG3, fontSize: 10, marginTop: 1 }}>{compareSub}</div>
          )}
        </div>
      )}
      {tip && info && (() => {
        const TIP_WIDTH = 280;
        const MARGIN = 12;
        const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
        const flipLeft = tip.x + MARGIN + TIP_WIDTH > vw - 8;
        const left = flipLeft ? Math.max(8, tip.x - MARGIN - TIP_WIDTH) : tip.x + MARGIN;
        return (
          <div style={{
            position: "fixed", left, top: tip.y - 36,
            width: TIP_WIDTH,
            background: "var(--bg-elevated, #fff)", border: "1px solid var(--border-default)",
            borderRadius: 7, padding: "6px 10px", fontSize: 12, pointerEvents: "none",
            whiteSpace: "normal", lineHeight: 1.4,
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)", color: FG1, zIndex: 20,
          }}>
            {info}
          </div>
        );
      })()}
    </div>
  );
}

export function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="legend-dot">
      <span className="sw" style={{ background: color }} />
      {label}
    </span>
  );
}

type LabelDatum = { label: string; value: number };

export function HBarChart({
  title, subtitle, data, color = PRIMARY, total, titleExtra, filterPills, headerExtra,
}: { title: string; subtitle?: string; data: LabelDatum[]; color?: string; total?: number } & CardProps) {
  const denom = total ?? data.reduce((s, d) => s + d.value, 0);
  if (data.length === 0) return <EmptyChart title={title} subtitle={subtitle} titleExtra={titleExtra} filterPills={filterPills} headerExtra={headerExtra} />;
  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 30)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 110, bottom: 4, left: 10 }}>
          <XAxis type="number" hide />
          <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: FG1 }} width={130} interval={0} />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.03)" }}
            formatter={(value) => {
              const n = Number(value);
              return [`${fmtN(n)} · ${fmtPct(n, denom)}`, "Users"] as [string, string];
            }}
          />
          <Bar dataKey="value" fill={color} radius={[0, 3, 3, 0]} isAnimationActive={false}>
            <LabelList
              dataKey="value"
              position="right"
              formatter={(v) => `${fmtN(Number(v))} (${fmtPct(Number(v), denom)})`}
              style={{ fontSize: 10, fill: MUTED }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function VBarChart({
  title, subtitle, data, color = PRIMARY, filterPills, headerExtra, titleExtra,
}: { title: string; subtitle?: string; data: LabelDatum[]; color?: string } & CardProps) {
  if (data.length === 0) return <EmptyChart title={title} subtitle={subtitle} filterPills={filterPills} headerExtra={headerExtra} titleExtra={titleExtra} />;
  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 20, right: 8, left: 8, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} interval={0} />
          <YAxis tick={{ fontSize: 10, fill: MUTED }} allowDecimals={false} width={28} />
          <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
          <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} isAnimationActive={false}>
            <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: MUTED }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

type GroupedDatum = { label: string; demo: number; self: number; demoTotal: number; selfTotal: number };

export function GroupedHBarChart({
  title, subtitle, data, filterPills, headerExtra, titleExtra,
}: { title: string; subtitle?: string; data: GroupedDatum[] } & CardProps) {
  if (data.length === 0) return <EmptyChart title={title} subtitle={subtitle} filterPills={filterPills} headerExtra={headerExtra} titleExtra={titleExtra} />;
  const demoTotal = data[0]?.demoTotal ?? 0;
  const selfTotal = data[0]?.selfTotal ?? 0;

  const transformed = data.map((d) => ({
    label: d.label,
    demoPct: pctVal(d.demo, d.demoTotal),
    selfPct: pctVal(d.self, d.selfTotal),
    demoCount: d.demo,
    selfCount: d.self,
  }));

  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <div className="legend">
        <LegendDot color={PRIMARY} label={`Had demo (n=${demoTotal})`} />
        <LegendDot color={VIOLET} label={`Self-serve (n=${selfTotal})`} />
      </div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 56)}>
        <BarChart data={transformed} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 10 }}>
          <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: MUTED }} />
          <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: FG1 }} width={130} interval={0} />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.03)" }}
            formatter={(value, name, item) => {
              const pct = Number(value);
              const row = item?.payload as { demoCount?: number; selfCount?: number } | undefined;
              const isDemo = name === "demoPct";
              const count = (isDemo ? row?.demoCount : row?.selfCount) ?? 0;
              const total = isDemo ? demoTotal : selfTotal;
              return [`${pct.toFixed(1)}% (${count}/${total})`, isDemo ? "Demo" : "Self"] as [string, string];
            }}
          />
          <Bar dataKey="demoPct" name="demoPct" fill={PRIMARY} radius={[0, 3, 3, 0]} isAnimationActive={false} />
          <Bar dataKey="selfPct" name="selfPct" fill={VIOLET} radius={[0, 3, 3, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

type DonutDatum = { label: string; value: number; color: string };

export function DonutChart({
  title, subtitle, data, filterPills, headerExtra, titleExtra,
}: { title: string; subtitle?: string; data: DonutDatum[] } & CardProps) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <div style={{ width: 150, height: 150, position: "relative", flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={42}
                outerRadius={70}
                strokeWidth={0}
                isAnimationActive={false}
              >
                {data.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip
                formatter={(value) => {
                  const n = Number(value);
                  return [`${fmtN(n)} · ${fmtPct(n, total)}`, "Users"] as [string, string];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", fontSize: 16, fontWeight: 700, color: FG1 }}>
            {fmtN(total)}
          </div>
        </div>
        <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 4 }}>
          {data.map((d) => (
            <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 10, height: 10, background: d.color, borderRadius: 2, display: "inline-block" }} />
              <span>{d.label}</span>
              <span style={{ color: FG3 }}>· {d.value} · {fmtPct(d.value, total)}</span>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}

export function TrendLineChart({
  title, subtitle, data, color = PRIMARY, filterPills, headerExtra, titleExtra,
}: { title: string; subtitle?: string; data: LabelDatum[]; color?: string } & CardProps) {
  if (data.length === 0) return <EmptyChart title={title} subtitle={subtitle} filterPills={filterPills} headerExtra={headerExtra} titleExtra={titleExtra} />;
  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <ResponsiveContainer width="100%" height={200}>
        <RechartsLineChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} />
          <YAxis tick={{ fontSize: 10, fill: MUTED }} allowDecimals={false} width={32} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={{ r: 3, fill: color }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
        </RechartsLineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

type RateDatum = { label: string; value: number; denom: number };

export function RateBarList({
  title, subtitle, data, color = PRIMARY, filterPills, headerExtra, titleExtra,
}: { title: string; subtitle?: string; data: RateDatum[]; color?: string } & CardProps) {
  if (data.length === 0) return <EmptyChart title={title} subtitle={subtitle} filterPills={filterPills} headerExtra={headerExtra} titleExtra={titleExtra} />;
  const transformed = data.map((d) => ({
    label: d.label,
    rate: pctVal(d.value, d.denom),
    value: d.value,
    denom: d.denom,
  }));
  return (
    <ChartCard
      title={titleExtra ? <>{title} {titleExtra}</> : title}
      subtitle={subtitle}
      filterPills={filterPills}
      headerExtra={headerExtra}
    >
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 30)}>
        <BarChart data={transformed} layout="vertical" margin={{ top: 4, right: 110, bottom: 4, left: 10 }}>
          <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: MUTED }} />
          <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: FG1 }} width={130} interval={0} />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.03)" }}
            formatter={(value, _name, item) => {
              const pct = Number(value);
              const row = item?.payload as { value?: number; denom?: number } | undefined;
              const v = row?.value ?? 0;
              const d = row?.denom ?? 0;
              return [`${pct.toFixed(1)}% (${v}/${d})`, "Rate"] as [string, string];
            }}
          />
          <Bar dataKey="rate" fill={color} radius={[0, 3, 3, 0]} isAnimationActive={false}>
            <LabelList
              dataKey="rate"
              position="right"
              content={(props) => {
                const { x, y, width, height, index } = props as {
                  x?: number; y?: number; width?: number; height?: number; index?: number;
                };
                if (x == null || y == null || width == null || height == null || index == null) return null;
                const row = transformed[index];
                if (!row) return null;
                return (
                  <text
                    x={x + width + 6}
                    y={y + height / 2 + 3}
                    fontSize={10}
                    fill={MUTED}
                  >
                    {row.rate.toFixed(1)}% ({row.value}/{row.denom})
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
